import toast from 'react-hot-toast';
import { client } from '@/services/api/axios.ts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { TDeviceOverviewConfig } from '@/features/devices/hooks/queries/[device]/get-device-overview.ts';

type UpdateDeviceOverviewConfigRequest = {
  deviceId: string;
  plantId?: string;
  config: TDeviceOverviewConfig;
};

type UpdateDeviceOverviewConfigResponse = {
  body: {
    config: TDeviceOverviewConfig;
  };
};

type UseUpdateDeviceOverviewConfigOptions = {
  onSuccess?: (data: UpdateDeviceOverviewConfigResponse['body']) => void;
  onError?: (error: Error) => void;
  showToast?: boolean;
};

export const useUpdateDeviceOverviewConfig = ({
  onSuccess,
  onError,
  showToast = true,
}: UseUpdateDeviceOverviewConfigOptions = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ deviceId, config }: UpdateDeviceOverviewConfigRequest) => {
      const { data } = await client.post<UpdateDeviceOverviewConfigResponse>(
        `/devices/${deviceId}/overview/config`,
        { config }
      );

      return data.body;
    },
    onSuccess: (data) => {
      if (showToast) {
        toast.success('Device fields configuration updated successfully');
      }
      onSuccess?.(data);
    },
    onError: (error: Error) => {
      if (showToast) {
        toast.error('Failed to update device fields configuration');
      }
      onError?.(error);
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['device', variables.deviceId, 'overview'],
      });

      if (variables.plantId) {
        queryClient.invalidateQueries({
          queryKey: ['plants', variables.plantId, 'devices', 'overview'],
        });
      }
    },
  });
};
